/* eslint-disable immutable/no-mutation */
/**
 * Script injection utilities for native launch experience
 *
 * In LTI launches, these scripts are injected via launch.html.erb.
 * For native launches, we need to inject them dynamically from the React app.
 */

interface WindowGlobals {
  locale?: string
  moment_locale?: string
  launch_params?: Record<string, unknown>
}

/**
 * Injects a script tag into the document head
 */
function injectScript(src: string, async = true, id?: string): Promise<void> {
  return new Promise((resolve, reject) => {
    const script = document.createElement('script')
    script.src = src
    script.async = async
    script.type = 'text/javascript'

    if (id) {
      script.id = id
    }

    script.onload = () => resolve()
    script.onerror = () => reject(new Error(`Failed to load script: ${src}`))

    const firstScript = document.getElementsByTagName('script')[0]
    if (firstScript?.parentNode) {
      firstScript.parentNode.insertBefore(script, firstScript)
    } else {
      document.head.appendChild(script)
    }
  })
}

/**
 * Sets up window globals required by the application
 */
export function setupWindowGlobals(globals: WindowGlobals): void {
  if (globals.locale) {
    window.locale = globals.locale
  }

  if (globals.moment_locale) {
    window.moment_locale = globals.moment_locale
  }

  if (globals.launch_params) {
    window.launch_params = globals.launch_params
  }
}

/**
 * Loads the Learnosity assessment rendering engine
 */
export async function loadLearnosityScript(): Promise<void> {
  const learnosityDomain = process.env.LEARNOSITY_DOMAIN || 'https://questions.learnosity.com'
  const learnosityUrl = `${learnosityDomain}?v2023.2.LTS`

  try {
    await injectScript(learnosityUrl, false, 'nq-injected-learnosity') // Learnosity should load synchronously
  } catch (error) {
    console.error('[ScriptInjection] Failed to load Learnosity:', error)
    throw error
  }
}

interface PendoConfig {
  enabled: boolean
  pendoId?: string
  currentUser?: {
    uuid: string
    roles: string[]
    account_uuid: string
  }
  locale?: string
}

/**
 * Loads and initializes Pendo analytics
 */
export async function loadPendoAnalytics(config: PendoConfig): Promise<void> {
  if (!config.enabled || !config.pendoId || !config.currentUser) {
    console.warn('[ScriptInjection] Pendo analytics disabled or missing configuration')
    return
  }

  try {
    // Initialize Pendo queue
    window.pendo = window.pendo || {}
    window.pendo._q = window.pendo._q || []

    const methods = ['initialize', 'identify', 'updateOptions', 'pageLoad', 'track']
    methods.forEach(method => {
      window.pendo[method] =
        window.pendo[method] ||
        function (...args: unknown[]) {
          const queue = method === methods[0] ? 'unshift' : 'push'
          window.pendo._q[queue]([method, ...args])
        }
    })

    // Load Pendo script
    const pendoUrl = `https://cdn.pendo.io/agent/static/${config.pendoId}/pendo.js`
    await injectScript(pendoUrl, true, 'nq-injected-pendo')

    // Initialize Pendo with user data
    const hashedUserId = await hashString(config.currentUser.uuid)

    window.pendo.initialize({
      visitor: {
        id: hashedUserId,
        roles: JSON.stringify(config.currentUser.roles),
      },
      account: {
        id: config.currentUser.account_uuid,
      },
      locale: config.locale || window.locale,
    })
  } catch (error) {
    console.error('[ScriptInjection] Failed to load Pendo analytics:', error)
  }
}

/**
 * Simple SHA-256 hash function for user ID
 */
async function hashString(str: string): Promise<string> {
  const encoder = new TextEncoder()
  const data = encoder.encode(str)
  const hashBuffer = await crypto.subtle.digest('SHA-256', data)
  const hashArray = Array.from(new Uint8Array(hashBuffer))
  const hashHex = hashArray.map(b => b.toString(16).padStart(2, '0')).join('')
  return hashHex
}

/**
 * Main function to initialize all required scripts for native launch
 */
export async function initializeNativeLaunchScripts(options: {
  locale?: string
  momentLocale?: string
  launchParams?: Record<string, unknown>
  pendoConfig?: PendoConfig
  loadLearnosity?: boolean
}): Promise<void> {
  setupWindowGlobals({
    locale: options.locale,
    moment_locale: options.momentLocale,
    launch_params: options.launchParams,
  })

  const scriptPromises: Promise<void>[] = []

  if (options.loadLearnosity !== false) {
    scriptPromises.push(loadLearnosityScript())
  }

  if (options.pendoConfig) {
    scriptPromises.push(loadPendoAnalytics(options.pendoConfig))
  }

  try {
    await Promise.all(scriptPromises)
  } catch (error) {
    console.error('[ScriptInjection] Error initializing scripts:', error)
  }
}

declare global {
  interface Window {
    locale: string
    moment_locale: string
    launch_params: Record<string, unknown>
    pendo: {
      _q: unknown[]
      initialize: (config: unknown) => void
      identify: (...args: unknown[]) => void
      updateOptions: (...args: unknown[]) => void
      pageLoad: (...args: unknown[]) => void
      track: (...args: unknown[]) => void
      [key: string]: unknown
    }
    LearnosityApp?: unknown
  }
}
